import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/**
 * Radix AlertDialog wrapper — destructive confirmations only (delete a
 * contact, delete a household). General-purpose modals belong in `Dialog`.
 *
 * Surface tiers per design.md §8:
 *   overlay → glass-surface-strong dim
 *   content → glass-surface-strong rounded-2xl
 */
export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;
export const AlertDialogCancel = AlertDialogPrimitive.Cancel;
export const AlertDialogAction = AlertDialogPrimitive.Action;

export const AlertDialogOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(function AlertDialogOverlay({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-overlay",
        "bg-black/30 backdrop-blur-sm",
        className,
      )}
      {...props}
    />
  );
});

/**
 * Mirrors `Dialog`'s `dialogContent`: each `size` is a single `max-width` that
 * folds the cap and the universal 8px viewport gutter into one `min()` (the
 * "smaller value wins" rule, design.md §10). Keep it one max-width utility so
 * `twMerge` has nothing to collapse.
 */
const alertDialogContent = cva(
  [
    "fixed left-1/2 top-1/2 z-modal w-full -translate-x-1/2 -translate-y-1/2",
    "max-h-[calc(100vh_-_1rem)]",
    "rounded-2xl glass-surface-strong p-6",
    "flex flex-col gap-4",
  ],
  {
    variants: {
      size: {
        md: "max-w-[min(28rem,100vw_-_1rem)]",
        lg: "max-w-[min(32rem,100vw_-_1rem)]",
        "2xl": "max-w-[min(42rem,100vw_-_1rem)]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface AlertDialogContentProps
  extends ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>,
    VariantProps<typeof alertDialogContent> {}

export const AlertDialogContent = forwardRef<
  HTMLDivElement,
  AlertDialogContentProps
>(function AlertDialogContent({ className, size, ...props }, ref) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(alertDialogContent({ size }), className)}
        {...props}
      />
    </AlertDialogPortal>
  );
});

export function AlertDialogHeader({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>;
}

/**
 * Footer action row. Per design.md §10 it stacks to a full-width column on
 * mobile (primary action on top, DOM order) and lays out as a reversed row at
 * `md`+ (primary action at the trailing/right edge). Buttons go full width on
 * mobile and revert to content width on desktop.
 */
export function AlertDialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row-reverse [&>*]:w-full md:[&>*]:w-auto">
      {children}
    </div>
  );
}

export const AlertDialogTitle = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(function AlertDialogTitle({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn("type-heading-sm text-text", className)}
      {...props}
    />
  );
});

export const AlertDialogDescription = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(function AlertDialogDescription({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn("type-body-base text-text-muted", className)}
      {...props}
    />
  );
});
